import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { apiClient } from '../lib/api'
import type { ThreadTag } from '../lib/types'
import { useThreadStore } from '../stores/threadStore'

interface ThreadTagManagerProps {
  open: boolean
  threadId: number
  onClose: () => void
}

export default function ThreadTagManager({ open, threadId, onClose }: ThreadTagManagerProps) {
  const [tags, setTags] = useState<ThreadTag[]>([])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { fetchThreads } = useThreadStore()

  useEffect(() => {
    if (open) {
      loadTags()
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open, threadId])

  const loadTags = async () => {
    const data = await apiClient.get<ThreadTag[]>(`research/threads/${threadId}/tags/`)
    // Only show personal tags (not auto-generated)
    setTags(data.filter((t) => t.tag_type !== 'auto'))
  }

  const addTag = async () => {
    const name = inputValue.trim()
    if (!name || loading) return
    setLoading(true)
    try {
      const newTag = await apiClient.post<ThreadTag>(`research/threads/${threadId}/tags/`, { name })
      setTags((prev) => [...prev, newTag])
      setInputValue('')
      fetchThreads()
    } catch {
      // Tag might already exist (409) — just clear input
    }
    setLoading(false)
  }

  const removeTag = async (tag: ThreadTag) => {
    await apiClient.delete(`research/threads/${threadId}/tags/${tag.tag_id}/`)
    setTags((prev) => prev.filter((t) => t.id !== tag.id))
    fetchThreads()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" onClick={onClose}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="flex items-end justify-center h-full pb-8 px-4">
            <motion.div
              className="w-full max-w-sm bg-elevated rounded-2xl shadow-xl p-4"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ type: 'spring', damping: 25, stiffness: 400 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-sm font-semibold text-text-primary mb-3">Thread Tags</h3>

              {/* Existing tags */}
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-amber-500/10 text-amber-400 border border-amber-500/30"
                    >
                      {tag.tag_name}
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-0.5 hover:text-amber-200 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Add tag input */}
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add a tag..."
                  className="flex-1 bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted outline-none focus:border-amber-500/50"
                />
                <button
                  onClick={addTag}
                  disabled={!inputValue.trim() || loading}
                  className="px-3 py-2 rounded-lg text-sm font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 disabled:opacity-40 transition-colors"
                >
                  Add
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  )
}
